/**
 * realisticPinControls — sliders en vivo para los pins de la vista 3D.
 *
 * Limitación SDK: el `PinElement` de Google Maps Marker no expone API
 * para tocar el font-size del glyph aparte del `scale`; pasar HTMLElement
 * como glyph se rasteriza con `width:0 height:0` (verificado en runtime).
 * Por eso solo exponemos las 3 perillas que sí se aplican:
 *
 *  - pinOpacity: 0 (pin invisible) → 1 (opaco). Aplica a background y
 *    border. El glyph queda al `glyphOpacity` aparte.
 *  - glyphOpacity: opacidad del número.
 *  - pinScale: tamaño del pin Y del número (acoplados en este SDK).
 *
 * El store es global; `mountRealisticCabinetMarkers` se suscribe y
 * regenera los PinElement al cambiar cualquiera. Recargando la pestaña
 * los valores vuelven a su default; persistir entre sesiones es trivial
 * con `persist` middleware si se necesita más adelante.
 */
import { create } from 'zustand';

interface PinControlsState {
  pinOpacity: number;
  glyphOpacity: number;
  pinScale: number;
  /** color del número en hex (#rrggbb). */
  glyphColor: string;
  /** color del modelo 3D del regulador (rayo + halo) en hex. */
  modelColor: string;
  /** factor de escala del modelo Sentinel (glTF). 1 = tamaño real ~3.8 m. */
  modelScale: number;
  /** mostrar/ocultar la capa de reguladores (Sentinel + halo + pin). */
  regulatorsEnabled: boolean;
  /** mostrar/ocultar la capa de semáforos por grupo (~316 instancias). */
  lightsEnabled: boolean;
  /** auto-escalar el cabinet según el range de cámara: real (1×) en
   *  vista calle, exagerado (~10×) en vista municipio. Cuando está ON
   *  el slider `Mod. ×` queda informativo. */
  autoScale: boolean;
  /** altura sobre el terreno (m) de los cabezales de semáforo en la
   *  vista realista. Default 4 — altura física real aproximada de los
   *  cabezales DGT. Rango 0–8 para ajuste fino desde el panel. */
  lightAltitude: number;
  /** muestra etiquetas discretas con el ID compuesto (`cabinet.gN[.cardinal|·osm]`)
   *  encima de cada cabezal de semáforo. Modo mantenimiento — ARS93 las
   *  usa para construir la tabla real id→posición→azimut. Default OFF
   *  para no saturar al operador habitual. */
  lightIdsVisible: boolean;
  /** superpone la capa de tráfico TomTom (vector flow tiles → Polyline3D
   *  coloreadas por nivel de congestión) sobre la vista realista. Default
   *  OFF — el operador lo activa cuando quiere el dato. Refresh ~75 s con
   *  jitter. Misma TomTom key que la operativa MapLibre. */
  trafficFlowEnabled: boolean;
  setPinOpacity: (v: number) => void;
  setGlyphOpacity: (v: number) => void;
  setPinScale: (v: number) => void;
  setGlyphColor: (v: string) => void;
  setModelColor: (v: string) => void;
  setModelScale: (v: number) => void;
  setRegulatorsEnabled: (v: boolean) => void;
  setLightsEnabled: (v: boolean) => void;
  setAutoScale: (v: boolean) => void;
  setLightAltitude: (v: number) => void;
  setLightIdsVisible: (v: boolean) => void;
  setTrafficFlowEnabled: (v: boolean) => void;
}

export const usePinControls = create<PinControlsState>((set) => ({
  pinOpacity: 0.1,
  glyphOpacity: 1,
  pinScale: 1.3,
  glyphColor: '#42ff45',  // verde fluor — visible desde vista municipio/barrio
  modelColor: '#facc15',  // yellow-400
  modelScale: 2,          // 2× tamaño real cabinet (~3.2 m, ligeramente exagerado para visibilidad sin parecer torre)
  regulatorsEnabled: true,
  lightsEnabled: true,
  autoScale: true,
  lightAltitude: 4,
  lightIdsVisible: false,
  trafficFlowEnabled: false,
  setPinOpacity: (v) => set({ pinOpacity: v }),
  setGlyphOpacity: (v) => set({ glyphOpacity: v }),
  setPinScale: (v) => set({ pinScale: v }),
  setGlyphColor: (v) => set({ glyphColor: v }),
  setModelColor: (v) => set({ modelColor: v }),
  setModelScale: (v) => set({ modelScale: v }),
  setRegulatorsEnabled: (v) => set({ regulatorsEnabled: v }),
  setLightsEnabled: (v) => set({ lightsEnabled: v }),
  setAutoScale: (v) => set({ autoScale: v }),
  setLightAltitude: (v) => set({ lightAltitude: v }),
  setLightIdsVisible: (v) => set({ lightIdsVisible: v }),
  setTrafficFlowEnabled: (v) => set({ trafficFlowEnabled: v }),
}));

/**
 * Curva auto-scale del cabinet en función del `range` (m) de Map3D.
 *
 *   range ≤ 80 m     → scale 1×   (tamaño real, 1.62 m — vista calle)
 *   range ≈ 300 m    → scale ~3×  (vista cruce)
 *   range ≈ 800 m    → scale ~5×  (vista barrio)
 *   range ≥ 2500 m   → scale 10×  (vista municipio, máximo)
 *
 * Interpolación logarítmica entre los extremos para que la transición
 * sea suave al hacer zoom in/out.
 */
export function autoScaleForRange(range: number): number {
  const MIN_R = 80, MAX_R = 2500;
  const MIN_S = 1, MAX_S = 10;
  if (range <= MIN_R) return MIN_S;
  if (range >= MAX_R) return MAX_S;
  const t = Math.log(range / MIN_R) / Math.log(MAX_R / MIN_R);
  return MIN_S + t * (MAX_S - MIN_S);
}

/** Convierte "#rrggbb" → "r, g, b" para componer rgba(). */
export function hexToRgb(hex: string): string {
  const m = hex.replace('#', '').match(/.{2}/g);
  if (!m || m.length < 3) return '15, 23, 42';
  return m.slice(0, 3).map(h => parseInt(h, 16)).join(', ');
}
